import { FormikProps } from "formik";
import * as Yup from "yup";

import { CellProps, Row } from "react-table";
import { MessageDescriptor } from "react-intl";

export enum FieldType {
	Select,
	Multiselect,
	Text,
	Number,
	Currency,
	Percent,
	Date,
	Checkbox,
	Hidden
}

export const standardValidation: {
	[key in FieldType]: Yup.AnySchema;
} = {
	[FieldType.Checkbox]: Yup.boolean().nullable(),
	[FieldType.Select]: Yup.string().nullable(),
	[FieldType.Multiselect]: Yup.array().nullable(),
	[FieldType.Text]: Yup.string().nullable(),
	[FieldType.Number]: Yup.number()
		.transform(value => (isNaN(value) ? undefined : value))
		.nullable(),
	[FieldType.Currency]: Yup.number().nullable(),
	[FieldType.Percent]: Yup.number().nullable(),
	[FieldType.Date]: Yup.date().nullable(),
	[FieldType.Hidden]: Yup.mixed().nullable()
} as const;

export const requiredValidation: {
	[key in FieldType]: Yup.AnySchema;
} = {
	[FieldType.Checkbox]: Yup.boolean().required(),
	[FieldType.Select]: Yup.string().required(),
	[FieldType.Multiselect]: Yup.array().required().min(1),
	[FieldType.Text]: Yup.string().required(),
	[FieldType.Number]: Yup.number().required(),
	[FieldType.Currency]: Yup.number().required(),
	[FieldType.Percent]: Yup.number().required(),
	[FieldType.Date]: Yup.date().required(),
	[FieldType.Hidden]: Yup.mixed().nullable()
};

export interface IFormTableSchema<T extends {}> {
	readonly tableMetadata: TableMetadata<Partial<T>>;
	readonly columnMetadata: Partial<Record<keyof T, ColumnMetadata<T>>>;
}

export interface TableMetadata<RowData extends {}> {
	readonly dployTesterContext?: string;

	readonly isReadOnlyRow?: (rowData: RowData, cells: any) => boolean;
	readonly addRowButtonIsDisabled?: (
		formValues: Record<string, any>,
		row?: Row<RowData>["cells"] | undefined
	) => boolean;
	readonly isDeletableRow?: (rowData: RowData, cells: any) => boolean;
	readonly rowFilterer?: (
		allRows: any[],
		formValues?: Record<string, any> | undefined
	) => any[];
}

export interface ColumnMetadata<RowData extends {} = {}> {
	readonly fieldType: FieldType;
	readonly isMultiline?: boolean;
	// Defaults to 2 for percent and 0 for number/currency
	readonly minimumFractionDigits?: number;
	// Defaults to 2 for percent and 0 for number/currency
	readonly maximumFractionDigits?: number;
	readonly allowNegative?: boolean;
	readonly isRequired?: Boolean;
	readonly hide?: (
		value: CellProps<RowData>["value"],
		rowData: Row<RowData>["original"],
		cells: Row<RowData>["cells"],
		isPlaceholder: boolean,
		firstInGroup: boolean
	) => boolean;
	readonly displayValue?: (
		value: CellProps<RowData>["value"],
		rowData: Row<RowData>["original"],
		cells: Row<RowData>["cells"],
		isPlaceholder: boolean,
		firstInGroup: boolean,
		formValues: Record<string, any>
	) => any;

	/**
	 * Useful for when you need to localize the string value of the cell
	 * @param value Value of the cell
	 * @returns A "react-intl"-MessageDescriptor object to be used by `intl.formatMessage()`
	 */
	readonly displayValueIntlDescriptor?: (
		value: CellProps<RowData>["value"]
	) => MessageDescriptor;

	/**
	 * Whether the column-cells should always render an editable input-field without the user clicking on the row's edit-button first.
	 *
	 * An @see `isEditableFunctions` -entry that returns `false` will still disable this column even if this value is marked `true`.
	 */
	readonly alwaysInEditMode?: Boolean;
	readonly isEditableFunctions?: Record<
		string,
		(
			row: FormikProps<RowData>,
			columnCells: Row<RowData>["cells"],
			tableRows: RowData[]
		) => boolean
	>;
	readonly validatorSchemas?: Record<string, Yup.AnySchema>;
	readonly aggregate?: (tableRows: RowData[]) => number;
	readonly aggregateFooter?: (tableRows: RowData[]) => number;
	readonly defaultValue?: unknown;
	readonly defaultValueFunc?: () => unknown;
}
